import React, { useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChart as PieChartIcon } from 'lucide-react';

/**
 * Componente de 3 gráficas de pie para Timesheets
 * VERSIÓN FINAL: Leyendas abajo con scroll horizontal
 * MEJORADO: Leyenda clicable como filtro interactivo
 */
export default function GraficasPieTimesheets({
  tipoRegistroData = [],
  horasProyectoData = [],
  isDarkMode = true,
  onProyectoClick = null,
  onTipoRegistroClick = null,
  selectedProyectos = [],
  selectedTiposRegistro = []
}) {
  // Estilos
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  // Paleta verde inspirada en propuesta visual
  const FINOPS_COLORS = [
    '#00FF66',
    '#1EEB8A',
    '#34D399',
    '#2DD4BF',
    '#22C55E',
    '#10B981',
    '#4ADE80',
    '#6EE7B7',
    '#16A34A',
    '#059669'
  ];

  const COLORS_FACTURABLE = ['#00FF66', '#14532D'];

  // Preparar datos
  const dataTipoRegistro = tipoRegistroData.map(item => ({
    name: item.tipo || 'Sin tipo',
    value: parseFloat(item.horas) || 0
  }));

  const dataClientes = horasProyectoData.slice(0, 1000).map(item => ({
    name: item.proyecto || 'Sin cliente',
    value: parseFloat(item.horas) || 0
  }));

  const horasFacturables = tipoRegistroData
    .filter(item => {
      const tipo = (item.tipo || '').toLowerCase();
      return !tipo.includes('nofac') && !tipo.includes('ceco');
    })
    .reduce((sum, item) => sum + (parseFloat(item.horas) || 0), 0);

  const horasNoFacturables = tipoRegistroData
    .filter(item => {
      const tipo = (item.tipo || '').toLowerCase();
      return tipo.includes('nofac') || tipo.includes('ceco');
    })
    .reduce((sum, item) => sum + (parseFloat(item.horas) || 0), 0);

  const dataFacturable = [
    { name: 'Cliente', value: horasFacturables },
    { name: 'CECO', value: horasNoFacturables }
  ];

  // Custom Label
  const renderLabel = (entry) => {
    const percent = entry.percent * 100;
    if (percent > 5) {
      return `${percent.toFixed(1)}%`;
    }
    return '';
  };

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = data.payload.total || 0;
      const percent = total > 0 ? (data.value / total) * 100 : 0;
      
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40' 
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-semibold text-sm`}>{data.name}</p>
          <p className={`${textSecondary} text-sm`}>
            {data.value.toLocaleString('es-MX', { maximumFractionDigits: 2 })} horas
          </p>
          <p className={`${textMuted} text-xs`}>
            {percent.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Leyenda horizontal con scroll
  const CustomLegend = ({ data, colors, onItemClick = null, selectedValues = [] }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    const handleLegendItemClick = (itemName) => {
      if (onItemClick && typeof onItemClick === 'function') {
        onItemClick(itemName);
      }
    };

    return (
      <div 
        className="overflow-x-auto overflow-y-hidden pb-2 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full"
        style={{ 
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#00FF66 #052012' : '#10B981 #ECFDF5'
        }}
      >
        <div className="flex gap-4 min-w-max px-2">
          {data.map((entry, index) => {
            const percent = total > 0 ? (entry.value / total) * 100 : 0;
            const isSelected = selectedValues.includes(entry.name);
            
            return (
              <button
                key={`legend-${index}`}
                type="button"
                onClick={() => handleLegendItemClick(entry.name)}
                className={`flex items-center gap-2 flex-shrink-0 p-2 rounded-lg transition-all active:scale-95 ${
                  onItemClick ? 'cursor-pointer' : 'cursor-default'
                } ${
                  isSelected 
                    ? isDarkMode 
                      ? 'bg-[#00FF66]/20 border border-[#00FF66]/40' 
                      : 'bg-emerald-100 border border-emerald-400'
                    : isDarkMode
                      ? 'hover:bg-[#00FF66]/10'
                      : 'hover:bg-emerald-50'
                }`}
              >
                <div 
                  className="w-3 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <div className="flex flex-col">
                  <p 
                    className={`${textPrimary} text-xs font-medium whitespace-nowrap`}
                    title={entry.name}
                  >
                    {entry.name.length > 25 ? entry.name.substring(0, 25) + '...' : entry.name}
                  </p>
                  <p className={`${textMuted} text-xs whitespace-nowrap`}>
                    {entry.value.toLocaleString('es-MX', { maximumFractionDigits: 1 })} hrs · {percent.toFixed(1)}%
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Estado para manejar clicks en la gráfica
  const [clickedSector, setClickedSector] = React.useState(null);

  // Componente de gráfica individual
  const GraficaPie = ({ titulo, data, colors, onItemClick = null, selectedValues = [] }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const dataWithTotal = data.map(item => ({ ...item, total }));

    // Usar useEffect para llamar al callback cuando clickedSector cambia
    useEffect(() => {
      if (clickedSector && onItemClick && typeof onItemClick === 'function') {
        onItemClick(clickedSector);
        setClickedSector(null);
      }
    }, [onItemClick]);

    const handlePieClick = (data, index) => {
      if (onItemClick && typeof onItemClick === 'function') {
        onItemClick(data.name);
      }
    };

    return (
      <div className={`${cardClasses} p-4 flex flex-col`}>
        <div className="flex items-center justify-center gap-2 mb-4">
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-md border"
            style={{
              backgroundColor: isDarkMode ? 'rgba(0,255,102,0.15)' : 'rgba(0,255,102,0.1)',
              borderColor: isDarkMode ? 'rgba(0,255,102,0.4)' : 'rgba(0,255,102,0.35)'
            }}
          >
            <PieChartIcon className="w-3.5 h-3.5" style={{ color: '#00FF66' }} />
          </span>
          <h3 className={`${isDarkMode ? 'text-[#00FF66]' : textPrimary} text-sm font-bold`}>
            {titulo}
          </h3>
        </div>
        
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className={`${textSecondary} text-sm`}>No hay datos disponibles</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Gráfica de Pie */}
            <div className="flex items-center justify-center" style={{ cursor: onItemClick ? 'pointer' : 'default' }}>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={dataWithTotal}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderLabel}
                    outerRadius={95}
                    innerRadius={0}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={(state) => {
                      // Recharts onClick recibe un objeto con entry
                      if (state && state.entry) {
                        handlePieClick(state.entry);
                      } else if (state && state.name) {
                        handlePieClick({ name: state.name });
                      }
                    }}
                  >
                    {dataWithTotal.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={colors[index % colors.length]}
                        style={{ cursor: onItemClick ? 'pointer' : 'default' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Leyenda abajo con scroll horizontal */}
            <CustomLegend 
              data={data} 
              colors={colors}
              onItemClick={onItemClick}
              selectedValues={selectedValues}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <GraficaPie
        titulo="Tipo de Registro por horas"
        data={dataTipoRegistro}
        colors={FINOPS_COLORS}
        onItemClick={onTipoRegistroClick}
        selectedValues={selectedTiposRegistro}
      />

      <GraficaPie
        titulo="Horas por Cliente"
        data={dataClientes}
        colors={FINOPS_COLORS}
        onItemClick={onProyectoClick}
        selectedValues={selectedProyectos}
      />

      <GraficaPie
        titulo="Facturable"
        data={dataFacturable}
        colors={COLORS_FACTURABLE}
      />
    </div>
  );
}